import { Link } from 'react-router-dom';
import LegalPage from './LegalPage';

// DRAFT. Have counsel review before publishing, and resolve every <Fill>.
// Annex 2 (security measures) and Annex 3 (sub-processors) are commitments, so
// confirm each item reflects actual practice before this goes live, and keep
// Annex 3 current whenever a vendor that touches Customer Data is added.
function DataProcessingAddendum() {
  return (
    <LegalPage
      title="Data Processing Addendum"
      lead="How InfoRight processes personal data on behalf of its subscription customers."
      effectiveDate="September 30, 2026"
    >
      <section>
        <h2>1. Scope and incorporation</h2>
        <p>
          This Data Processing Addendum ("DPA") forms part of the{' '}
          <Link to="/legal/subscription-agreement">InfoRight SaaS Subscription Agreement</Link>{' '}
          (the "Agreement") between InfoRight, Inc. ("InfoRight") and the customer named in the
          applicable order ("Customer"). It applies whenever InfoRight processes Customer Personal
          Data while providing the Service. Capitalized terms not defined here have the meanings
          given in the Agreement.
        </p>
        <p>
          If this DPA and the Agreement conflict on the processing of Customer Personal Data, this
          DPA controls. On all other matters, the Agreement controls. Where the Standard Contractual
          Clauses apply under Section 13, they control over both.
        </p>
      </section>

      <section>
        <h2>2. Definitions</h2>
        <ul>
          <li>
            <strong>"Data Protection Laws"</strong> means all privacy and data protection laws that
            apply to the processing of Customer Personal Data under the Agreement. This includes the
            California Consumer Privacy Act as amended by the California Privacy Rights Act
            ("CCPA"), other U.S. state consumer privacy laws, and, where applicable, the EU General
            Data Protection Regulation ("GDPR"), the UK GDPR, and Canada's PIPEDA.
          </li>
          <li>
            <strong>"Customer Personal Data"</strong> means any personal data or personal
            information in Customer Data that InfoRight processes on Customer's behalf in providing
            the Service.
          </li>
          <li>
            <strong>"Data Subject"</strong> means the identified or identifiable individual to whom
            Customer Personal Data relates.
          </li>
          <li>
            <strong>"Security Incident"</strong> means a confirmed breach of security that leads to
            the accidental or unlawful destruction, loss, alteration, unauthorized disclosure of, or
            access to Customer Personal Data.
          </li>
          <li>
            <strong>"Sub-processor"</strong> means any third party InfoRight engages to process
            Customer Personal Data.
          </li>
          <li>
            "Controller", "processor", "business", "service provider", "processing", "sell", and
            "share" have the meanings given in the applicable Data Protection Laws.
          </li>
        </ul>
      </section>

      <section>
        <h2>3. Roles of the parties</h2>
        <p>
          Customer is the controller (or business) of Customer Personal Data. InfoRight is the
          processor (or service provider) and processes Customer Personal Data on Customer's
          behalf. Where Customer is itself a processor for a third party, such as an affiliate or a
          contractor's employer, Customer confirms that its instructions have been authorized by
          that third party, and InfoRight is Customer's sub-processor.
        </p>
        <p>
          Customer is responsible for the lawfulness of the Customer Personal Data it provides. This
          includes giving any required notices to, and obtaining any required consents from, its
          employees, contractors, and site visitors.
        </p>
      </section>

      <section>
        <h2>4. Processing instructions</h2>
        <p>
          InfoRight will process Customer Personal Data only according to Customer's documented
          instructions. The Agreement, this DPA, any order, and Customer's configuration and use of
          the Service together form Customer's complete instructions. The details of the processing
          are set out in Annex 1. Any additional instructions must be agreed in writing and may be
          subject to additional fees.
        </p>
        <p>
          InfoRight will promptly tell Customer if, in its opinion, an instruction violates Data
          Protection Laws. InfoRight may process Customer Personal Data outside Customer's
          instructions only where the law requires it. In that case, InfoRight will tell Customer
          first unless the law prohibits it.
        </p>
      </section>

      <section>
        <h2>5. U.S. state privacy law commitments</h2>
        <p>
          Where the CCPA or a similar U.S. state law applies, InfoRight is a service provider (or
          processor) and will not:
        </p>
        <ul>
          <li>sell or share Customer Personal Data;</li>
          <li>
            retain, use, or disclose Customer Personal Data for any purpose, including a commercial
            purpose, other than providing the Service under the Agreement, or as those laws
            otherwise allow;
          </li>
          <li>
            retain, use, or disclose Customer Personal Data outside the direct business relationship
            between InfoRight and Customer; or
          </li>
          <li>
            combine Customer Personal Data with personal information it receives from or on behalf
            of another person, or collects from its own interactions with individuals, except as
            those laws allow.
          </li>
        </ul>
        <p>
          InfoRight will comply with the obligations that apply to it under those laws and give
          Customer Personal Data the level of privacy protection they require. InfoRight will tell
          Customer if it can no longer meet these obligations. Customer may then take reasonable
          and appropriate steps to stop and remediate unauthorized processing. InfoRight certifies
          that it understands and will comply with the restrictions in this Section.
        </p>
      </section>

      <section>
        <h2>6. Confidentiality of personnel</h2>
        <p>
          InfoRight will make sure that everyone it authorizes to process Customer Personal Data is
          bound by appropriate confidentiality obligations. Access will be limited to personnel who
          need it to provide, support, or secure the Service.
        </p>
      </section>

      <section>
        <h2>7. Security</h2>
        <p>
          InfoRight will put in place and maintain appropriate technical and organizational
          measures to protect Customer Personal Data, as described in Annex 2. InfoRight may update
          these measures over time, but it will not materially reduce the overall security of the
          Service during a subscription term.
        </p>
        <p>
          Customer is responsible for using the Service securely. This includes managing user
          accounts and permissions, protecting credentials, and securing the devices its users use
          with the Service.
        </p>
      </section>

      <section>
        <h2>8. Sub-processors</h2>
        <p>
          Customer gives InfoRight general authorization to engage Sub-processors. The
          Sub-processors in use on the effective date are listed in Annex 3. InfoRight will:
        </p>
        <ul>
          <li>
            put a written agreement in place with each Sub-processor that protects data at least as
            well as this DPA;
          </li>
          <li>
            give Customer at least 30 days' notice before adding or replacing a
            Sub-processor, by updating Annex 3 and emailing Customer's designated contact; and
          </li>
          <li>remain responsible to Customer for its Sub-processors' performance.</li>
        </ul>
        <p>
          Customer may object to a new Sub-processor on reasonable data protection grounds by
          writing to InfoRight within the notice period. The parties will discuss a reasonable
          resolution in good faith. If they cannot agree, Customer may terminate the affected part
          of the Service and receive a pro-rata refund of prepaid fees for the rest of the term.
        </p>
      </section>

      <section>
        <h2>9. Data Subject requests and assistance</h2>
        <p>
          The Service lets Customer's administrators access, correct, export, and delete Customer
          Personal Data. If InfoRight receives a request directly from a Data Subject about Customer
          Personal Data, it will not respond except to refer the Data Subject to Customer, unless
          the law requires otherwise. It will forward the request to Customer promptly.
        </p>
        <p>
          Taking into account the nature of the processing and the information available to it,
          InfoRight will give Customer reasonable help to:
        </p>
        <ul>
          <li>respond to Data Subject requests;</li>
          <li>carry out data protection impact assessments; and</li>
          <li>consult regulators where Data Protection Laws require it.</li>
        </ul>
        <p>InfoRight may charge reasonable fees for help beyond the Service's standard features.</p>
      </section>

      <section>
        <h2>10. Security Incidents</h2>
        <p>
          InfoRight will notify Customer without undue delay after confirming a Security Incident,
          consistent with Section 6.3 of the Agreement. The notice will include what
          InfoRight then knows about the nature of the incident, the categories and approximate
          volume of data affected, the likely consequences, and the steps taken or proposed to
          contain it. InfoRight will add to the notice as more information becomes available.
        </p>
        <p>
          InfoRight will take reasonable steps to contain, investigate, and mitigate the Security
          Incident. It will reasonably cooperate with Customer so that Customer can meet any
          obligation to notify regulators or affected individuals. Notifying Customer of a Security
          Incident is not an admission of fault or liability.
        </p>
      </section>

      <section>
        <h2>11. Audits</h2>
        <p>
          On Customer's written request, and no more than once a year, InfoRight will provide
          information reasonably needed to show that it complies with this DPA. This will include
          answers to a reasonable security questionnaire and, where available, copies of relevant
          third-party audit reports or certifications.
        </p>
        <p>
          If that information is not enough to meet a requirement of Data Protection Laws, or a
          regulator requires it, Customer may carry out an audit, either itself or through an
          independent auditor bound by confidentiality. The audit must:
        </p>
        <ul>
          <li>be requested with at least 30 days' written notice;</li>
          <li>take place during normal business hours;</li>
          <li>avoid unreasonable disruption to InfoRight's operations; and</li>
          <li>be at Customer's expense.</li>
        </ul>
        <p>
          The Sub-processors' own audit reports will be used to show their compliance, rather than
          an audit of their facilities.
        </p>
      </section>

      <section>
        <h2>12. Return and deletion</h2>
        <p>
          For 60 days after the Agreement ends, Customer may export Customer Data using
          the Service's export features, or ask InfoRight to provide an export in a commonly used
          format. After that, InfoRight will delete Customer Personal Data from the Service within{' '}
          90 days, except where the law requires InfoRight to keep it. Copies in backups will be
          deleted as part of the normal backup rotation, within 365 days. Until they are deleted, they remain protected under this DPA.
        </p>
        <p>
          Customer is responsible for exporting and keeping any records it must maintain by law,
          such as MSHA training records, before the export period ends.
        </p>
      </section>

      <section>
        <h2>13. Data location and international transfers</h2>
        <p>
          InfoRight hosts and processes Customer Personal Data in the United States. InfoRight will
          not move the primary hosting location
          outside the United States without notifying Customer in advance.
        </p>
        <p>
          The GDPR, UK GDPR, or Swiss data protection law may apply to Customer Personal Data that
          is transferred to InfoRight in the United States. Where it does, the parties agree to the
          Standard Contractual Clauses approved by the European Commission in Decision 2021/914
          (Module Two, controller to processor, or Module Three, processor to processor, as
          applicable), which are incorporated by reference, together with the UK International Data
          Transfer Addendum or Swiss amendments where required. For the Clauses:
        </p>
        <ul>
          <li>the optional docking clause (Clause 7) applies;</li>
          <li>Option 2 (general authorization) applies under Clause 9;</li>
          <li>the optional language in Clause 11 does not apply;</li>
          <li>Clauses 17 and 18 are governed by the law of, and the courts of, Ireland;</li>
          <li>Annexes 1 to 3 of this DPA complete the Clauses' appendix.</li>
        </ul>
        <p>
          InfoRight offers these transfer terms to all customers, wherever they are located. Where
          the laws of another country require specific terms for transfers of personal data to the
          United States, InfoRight will agree appropriate terms with Customer on request. Customers
          who need a countersigned copy of the Standard Contractual Clauses or the UK Addendum can
          request one at <a href="mailto:support@inforight.com">support@inforight.com</a>.
        </p>
      </section>

      <section>
        <h2>14. Government requests</h2>
        <p>
          If a government authority asks InfoRight for Customer Personal Data, InfoRight will try
          to redirect the authority to Customer. If InfoRight is legally compelled to disclose the
          data, it will notify Customer promptly, unless the law prohibits this, so that Customer
          can seek a protective order or other remedy. InfoRight will disclose only the minimum
          data needed to comply.
        </p>
      </section>

      <section>
        <h2>15. Liability and term</h2>
        <p>
          Each party's liability under this DPA is subject to the limitations and exclusions of
          liability in the Agreement. This DPA stays in effect for as long as InfoRight processes
          Customer Personal Data, even after the Agreement ends.
        </p>
      </section>

      <section>
        <h2>Annex 1: Details of processing</h2>
        <ul>
          <li>
            <strong>Subject matter:</strong> providing the InfoRight subscription Service to
            Customer.
          </li>
          <li>
            <strong>Duration:</strong> the term of the Agreement, plus the return and deletion
            period in Section 12.
          </li>
          <li>
            <strong>Nature of processing:</strong> collection, storage, organization, retrieval,
            transmission, integration with Customer's systems, reporting, backup, and deletion.
          </li>
          <li>
            <strong>Purpose:</strong> to enable Customer to record, route, and report safety
            inspections, defects, training and certification records, site visitor approvals, and
            operational data, and to provide support.
          </li>
          <li>
            <strong>Data Subjects:</strong> Customer's employees, contractors, and field crews;
            site visitors; and personnel of other third parties whose information Customer enters
            into the Service or connects through integrations.
          </li>
          <li>
            <strong>Categories of personal data:</strong>
            <ul>
              <li>
                identification and contact details (name, work email, phone, employer, job role,
                assigned sites);
              </li>
              <li>
                training and certification records, including MSHA Form 5000-23 documentation and
                expiration dates;
              </li>
              <li>
                inspection and defect records, including who performed each inspection and when;
              </li>
              <li>visitor records (company, training acknowledgments, approvals, visit dates);</li>
              <li>account credentials, usage logs, device type, and IP address;</li>
              <li>
                inspection photos and sign-off signatures; device location.
              </li>
            </ul>
          </li>
          <li>
            <strong>Sensitive data:</strong> none intended. Customer must not submit sensitive data
            except as allowed by the{' '}
            <Link to="/legal/acceptable-use">Acceptable Use Policy</Link>.
          </li>
          <li>
            <strong>Frequency of transfer:</strong> continuous, for as long as Customer uses the
            Service.
          </li>
        </ul>
      </section>

      <section>
        <h2>Annex 2: Technical and organizational measures</h2>
        <ul>
          <li>
            <strong>Encryption:</strong> data is encrypted in transit using TLS 1.2 or higher, and
            at rest using AES-256 or an equivalent cloud provider standard.
          </li>
          <li>
            <strong>Access control:</strong> access is role-based and follows least privilege.
            Administrative access to production requires multi-factor authentication, and access is
            reviewed quarterly.
          </li>
          <li>
            <strong>Tenant isolation:</strong> each customer's data is logically separated, and
            access checks are enforced by the application.
          </li>
          <li>
            <strong>Logging and monitoring:</strong> administrative actions and access to
            production systems are logged, and logs are kept for 7 days.
          </li>
          <li>
            <strong>Resilience:</strong> the Service runs on Azure infrastructure with automated
            backups, which are kept for 365 days, and documented restore procedures.
          </li>
          <li>
            <strong>Secure development:</strong> code changes are peer-reviewed, dependencies are
            monitored for vulnerabilities, and secrets are kept out of source code and logs.
          </li>
          <li>
            <strong>Vulnerability management:</strong> security patches are applied promptly
            according to their severity, and the Service is tested for vulnerabilities annually.
          </li>
          <li>
            <strong>Personnel:</strong> personnel sign confidentiality agreements, receive security
            awareness training, and have their access removed promptly when they leave.
          </li>
          <li>
            <strong>Incident response:</strong> a documented incident response process supports
            the notification commitments in Section 10.
          </li>
          <li>
            <strong>Physical security:</strong> physical security is provided through the hosting
            provider's certified data centers. InfoRight does not operate its own data centers.
          </li>
        </ul>
      </section>

      <section>
        <h2>Annex 3: Sub-processors</h2>
        <div className="legal-table-wrap">
          <table className="legal-table">
            <thead>
              <tr>
                <th>Sub-processor</th>
                <th>Purpose</th>
                <th>Location</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>Amazon Web Services, Inc.</td>
                <td>Cloud hosting, storage, and infrastructure</td>
                <td>United States</td>
              </tr>
              <tr>
                <td>Twilio Inc. (SendGrid)</td>
                <td>Delivery of email notifications, such as defect alerts and approvals</td>
                <td>United States</td>
              </tr>
              <tr>
                <td>Twilio Inc.</td>
                <td>Delivery of SMS text notifications, such as defect alerts and approvals</td>
                <td>United States</td>
              </tr>
              <tr>
                <td>Microsoft Azure, Inc.</td>
                <td>Cloud hosting, storage, and infrastructure</td>
                <td>United States</td>
              </tr>
              <tr>
                <td>Stripe, Inc.</td>
                <td>Payment processing and billing</td>
                <td>United States</td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>
    </LegalPage>
  );
}

export default DataProcessingAddendum;
